import Swal from 'sweetalert2';

const svgIcon = {
  error: `<div class="cfm-icon-wrap error">
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
      stroke="#E24B4A" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg></div>`,
  success: `<div class="cfm-icon-wrap success">
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
      stroke="#3B6D11" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg></div>`,
  warning: `<div class="cfm-icon-wrap warning">
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
      stroke="#BA7517" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg></div>`,
  info: `<div class="cfm-icon-wrap info">
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
      stroke="#185FA5" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg></div>`
};

const base = {
  background: '#ffffff',
  // CRITICAL: Disable ALL Swal focus management
  focusConfirm: false,
  focusCancel: false,
  returnFocus: false,
  // Prevent Enter key from immediately triggering buttons
  allowEnterKey: false,
  customClass: {
    popup: 'cfm-popup',
    title: 'cfm-title',
    htmlContainer: 'cfm-text',
    icon: 'cfm-icon',
    confirmButton: 'cfm-btn-confirm',
    cancelButton: 'cfm-btn-cancel',
    actions: 'cfm-actions'
  },
  didOpen: () => {
    const container = document.querySelector('.swal2-container');
    if (container) container.style.zIndex = '2000';

    const popup = Swal.getPopup();
    if (popup) {
      const confirmButton = popup.querySelector('.cfm-btn-confirm');
      const cancelButton = popup.querySelector('.cfm-btn-cancel');

      if (confirmButton && cancelButton) {
        // Set initial tab order
        confirmButton.setAttribute('tabindex', '0');
        cancelButton.setAttribute('tabindex', '0');

        // Add a small delay before enabling button interaction
        let buttonsEnabled = false;
        const enableButtons = () => {
          buttonsEnabled = true;
          confirmButton.focus();
        };

        // Delay enabling buttons to prevent immediate Enter key triggering
        setTimeout(enableButtons, 200);

        // Handle keyboard at document level for better control
        const handleKeyDown = (e) => {
          // Only handle if this popup is the topmost one
          const topContainer = document.querySelector('.swal2-container:last-of-type');
          if (!topContainer || !topContainer.contains(popup)) {
            document.removeEventListener('keydown', handleKeyDown);
            return;
          }

          if (!buttonsEnabled) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }

          const activeElement = document.activeElement;

          // Arrow key navigation
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
            if (activeElement === confirmButton || activeElement === popup) {
              cancelButton.focus();
            } else if (activeElement === cancelButton) {
              confirmButton.focus();
            }
            return;
          }

          if (e.key === 'ArrowUp') {
            e.preventDefault();
            e.stopPropagation();
            if (activeElement === cancelButton) {
              confirmButton.focus();
            } else if (activeElement === confirmButton) {
              cancelButton.focus();
            }
            return;
          }

          // Tab navigation
          if (e.key === 'Tab') {
            e.preventDefault();
            e.stopPropagation();
            if (activeElement === confirmButton) {
              cancelButton.focus();
            } else {
              confirmButton.focus();
            }
            return;
          }

          // Enter key - click the focused button
          if (e.key === 'Enter') {
            if (activeElement === confirmButton || activeElement === cancelButton) {
              e.preventDefault();
              e.stopPropagation();
              activeElement.click();
            }
            return;
          }

          // Escape key - close the popup
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            Swal.close();
            return;
          }
        };

        // Add event listener to document
        document.addEventListener('keydown', handleKeyDown, true); // Use capture phase

        // Prevent focus from escaping to MUI dialog
        const preventFocusEscape = (e) => {
          if (!popup.contains(e.target)) {
            setTimeout(() => {
              if (document.activeElement === confirmButton || document.activeElement === cancelButton) {
                return;
              }
              confirmButton.focus();
            }, 0);
          }
        };

        document.addEventListener('focusin', preventFocusEscape, true);

        // Clean up when popup closes
        const cleanup = () => {
          document.removeEventListener('keydown', handleKeyDown, true);
          document.removeEventListener('focusin', preventFocusEscape, true);
        };

        // Use MutationObserver for cleanup
        const observer = new MutationObserver(() => {
          if (!document.contains(popup)) {
            cleanup();
            observer.disconnect();
          }
        });
        observer.observe(document.body, { childList: true, subtree: true });
      }
    }

    if (!document.getElementById('cfm-swal-styles')) {
      const style = document.createElement('style');
      style.id = 'cfm-swal-styles';
      style.textContent = `
        /* ---------- Overlay ---------- */
        .swal2-container.swal2-backdrop-show {
          background: rgba(0, 0, 0, 0.42) !important;
        }

        /* ---------- Popup card ---------- */
        .cfm-popup {
          border-radius: 10px !important;
          padding: 2rem 1.75rem 1.75rem !important;
          width: 380px !important;
          box-shadow: 0 8px 40px rgba(0,0,0,0.10) !important;
          font-family: inherit !important;
          outline: none !important;
        }

        /* swal2-icon wrapper — strip all default styling, our SVG div handles everything */
        .cfm-popup .swal2-icon {
          border: none !important;
          width: auto !important;
          height: auto !important;
          margin: 0 auto 1.1rem !important;
          overflow: visible !important;
          background: transparent !important;
        }
        .cfm-popup .swal2-icon::before,
        .cfm-popup .swal2-icon::after { display: none !important; }
        .cfm-popup .swal2-icon .swal2-x-mark,
        .cfm-popup .swal2-icon .swal2-x-mark-line-left,
        .cfm-popup .swal2-icon .swal2-x-mark-line-right,
        .cfm-popup .swal2-icon .swal2-success-ring,
        .cfm-popup .swal2-icon [class^='swal2-success-line'] { display: none !important; }

        /* our custom SVG circle — used by all iconHtml variants */
        .cfm-icon-wrap {
          width: 64px !important;
          height: 64px !important;
          border-radius: 50% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .cfm-icon-wrap.error   { background: #FCEBEB !important; }
        .cfm-icon-wrap.success { background: #EAF3DE !important; }
        .cfm-icon-wrap.warning { background: #FFF3CD !important; }
        .cfm-icon-wrap.info    { background: #E6F1FB !important; }

        /* ---------- Title ---------- */
        .cfm-title {
          font-size: 18px !important;
          font-weight: 600 !important;
          color: #111111 !important;
          padding: 0 0 0.35rem !important;
          margin: 0 !important;
          text-align: center !important;
        }

        /* ---------- Body text ---------- */
        .cfm-text {
          font-size: 13.5px !important;
          color: #666666 !important;
          line-height: 1.65 !important;
          margin: 0 !important;
          padding: 0 0.5rem !important;
          text-align: center !important;
        }

        /* ---------- Actions wrapper ---------- */
        .cfm-actions {
          flex-direction: column !important;
          gap: 8px !important;
          margin-top: 1.35rem !important;
          padding: 0 !important;
          width: 100% !important;
        }

        /* ---------- Confirm button ---------- */
        .cfm-btn-confirm {
          width: 100% !important;
          padding: 11px 0 !important;
          border-radius: 10px !important;
          font-size: 14px !important;
          font-weight: 500 !important;
          border: none !important;
          cursor: pointer !important;
          margin: 0 !important;
          transition: opacity 0.15s, box-shadow 0.15s !important;
          box-shadow: none !important;
          outline: none !important;
        }
        .cfm-btn-confirm:hover { opacity: 0.87 !important; }
        .cfm-btn-confirm:focus,
        .cfm-btn-confirm:focus-visible {
          outline: none !important;
          box-shadow: 
            0 0 0 2px rgba(255,255,255,0.9),
            0 0 0 4px rgba(226, 75, 74, 0.25) !important;
          transform: scale(1.01);
        }

        /* ---------- Cancel button ---------- */
        .cfm-btn-cancel {
          width: 100% !important;
          padding: 11px 0 !important;
          border-radius: 10px !important;
          font-size: 14px !important;
          font-weight: 400 !important;
          border: 1px solid #e0e0e0 !important;
          background: transparent !important;
          color: #333333 !important;
          cursor: pointer !important;
          margin: 0 !important;
          box-shadow: none !important;
          transition: background 0.15s, box-shadow 0.15s !important;
          outline: none !important;
        }
        .cfm-btn-cancel:hover  { background: #f5f5f5 !important; }
        .cfm-btn-cancel:focus,
        .cfm-btn-cancel:focus-visible {
          outline: none !important;
          background: #f8f8f8 !important;
          border-color: #d5d5d5 !important;
          box-shadow: 
            0 0 0 2px rgba(255,255,255,0.9),
            0 0 0 4px rgba(0,0,0,0.08) !important;
          }

        /* Outer icon ring */
        .cfm-icon-wrap { position: relative; }
        .cfm-icon-wrap::before {
          content: '';
          position: absolute;
          inset: -5px;
          border-radius: 50%;
          opacity: 0.18;
        }
        .cfm-icon-wrap.error::before   { background: #E24B4A; }
        .cfm-icon-wrap.success::before { background: #3B6D11; }
        .cfm-icon-wrap.warning::before { background: #BA7517; }
        .cfm-icon-wrap.info::before    { background: #185FA5; }

        /* ---------- Animations ---------- */
        .swal2-show  { animation: cfm-in  0.18s ease both !important; }
        .swal2-hide  { animation: cfm-out 0.14s ease both !important; }
        @keyframes cfm-in  { from { opacity:0; transform: scale(0.95) translateY(8px) } to { opacity:1; transform: scale(1) translateY(0) } }
        @keyframes cfm-out { from { opacity:1; transform: scale(1) } to { opacity:0; transform: scale(0.95) } }
      `;
      document.head.appendChild(style);
    }
  },
  willOpen: () => {
    // Temporarily disable MUI Dialog's focus trap
    const muiDialogs = document.querySelectorAll('.MuiDialog-root');
    muiDialogs.forEach((dialog) => {
      dialog.setAttribute('inert', '');
      const dialogContainer = dialog.querySelector('[role="dialog"]');
      if (dialogContainer) {
        dialogContainer.setAttribute('tabindex', '-1');
      }
    });

    // Disable other Swal instances
    const otherContainers = document.querySelectorAll('.swal2-container:not(:last-of-type)');
    otherContainers.forEach((container) => {
      container.setAttribute('inert', '');
    });
  },
  willClose: () => {
    // Re-enable MUI Dialog
    const muiDialogs = document.querySelectorAll('.MuiDialog-root[inert]');
    muiDialogs.forEach((dialog) => {
      dialog.removeAttribute('inert');
    });

    // Re-enable other Swal instances
    const otherContainers = document.querySelectorAll('.swal2-container[inert]');
    otherContainers.forEach((container) => {
      container.removeAttribute('inert');
    });
  }
};
/* ─────────────────────────────────────────
   DANGER / DELETE / CLEAR  (red button)
───────────────────────────────────────── */
export const ConfirmDeleteClear = async (title, text) => {
  const result = await Swal.fire({
    ...base,
    title,
    text,
    iconHtml: svgIcon.warning,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#E24B4A',
    allowOutsideClick: false
  });

  return result.isConfirmed;
};

/* ─────────────────────────────────────────
   ERROR + RELOAD
───────────────────────────────────────── */
export const ErrorFetchData = async (title, text) => {
  const result = await Swal.fire({
    ...base,
    title,
    text,
    iconHtml: svgIcon.error,
    showCancelButton: true,
    confirmButtonText: 'Reload',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#E24B4A',
    allowOutsideClick: false
  });
  if (result.isConfirmed) window.location.reload();
};

/* ─────────────────────────────────────────
   SUCCESS PAYMENT
───────────────────────────────────────── */
export const SuccessPayment = (title, text) =>
  Swal.fire({
    ...base,
    title,
    text,
    iconHtml: svgIcon.success,
    confirmButtonText: 'OK',
    confirmButtonColor: '#3B6D11',
    allowOutsideClick: false
  });

/* ─────────────────────────────────────────
   SUCCESS ALERT (generic)
───────────────────────────────────────── */
export const SuccessAlert = (title, text) =>
  Swal.fire({
    ...base,
    title,
    text,
    iconHtml: svgIcon.success,
    confirmButtonText: 'OK',
    confirmButtonColor: '#3B6D11',
    allowOutsideClick: false
  });

/* ─────────────────────────────────────────
   ERROR — UPDATE PRODUCTS
───────────────────────────────────────── */
export const ErrorUpdateProducts = (title, text) =>
  Swal.fire({
    ...base,
    title,
    text,
    iconHtml: svgIcon.warning,
    showCancelButton: true,
    confirmButtonText: 'OK',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#E24B4A',
    allowOutsideClick: false
  });

/* ─────────────────────────────────────────
   NO DATA FOUND
───────────────────────────────────────── */
export const NoDataFound = (title, text) =>
  Swal.fire({
    ...base,
    title,
    text,
    iconHtml: svgIcon.error,
    confirmButtonText: 'OK',
    confirmButtonColor: '#E24B4A',
    allowOutsideClick: false
  });

/* ─────────────────────────────────────────
   SUCCESS CONFIRMATION  (blue button)
───────────────────────────────────────── */
export const SuccessConfirmation = async (title, text) => {
  const result = await Swal.fire({
    ...base,
    title,
    text,
    iconHtml: svgIcon.warning,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#185FA5',
    allowOutsideClick: false
  });

  return result.isConfirmed;
};
